import React, { useState } from 'react';
import { AIHealthCoachProps } from '../../../types/health-tools/engagement-types';
import HealthCard from '../ui/HealthCard';
import ProgressBar from '../ui/ProgressBar';
import { MessageSquare, Users, Calendar, Activity, Trophy, Zap } from 'lucide-react';

// Define additional types needed for the component
export interface HealthCoachMessage {
  id?: string;
  sender: 'user' | 'coach';
  content: string;
  timestamp?: Date;
  suggestions?: string[];
}

export interface HealthFocusArea {
  name: string;
  priority: string;
  score: number;
}

// Mock conversation data since we only have conversation IDs in the props
const mockConversationMessages: Record<string, HealthCoachMessage[]> = {
  'conv1': [
    {
      id: '1',
      sender: 'coach',
      content: 'Welcome back! How have you been tracking with your daily water intake goal?',
      suggestions: ["I've been doing well", "I've been struggling", 'I want to change my goal']
    },
    {
      id: '2',
      sender: 'user',
      content: "I've been struggling a bit this week."
    },
    {
      id: '3',
      sender: 'coach',
      content: "That's okay! Small steps lead to big changes. Would you like some tips to help you remember to drink water throughout the day?",
      suggestions: ['Yes, please', 'No thanks', 'I need a different type of help']
    }
  ]
};

// Mock focus areas
const mockFocusAreas: HealthFocusArea[] = [
  { name: 'Daily Water Intake', priority: 'High Priority', score: 40 },
  { name: 'Physical Activity', priority: 'Medium Priority', score: 65 },
  { name: 'Sleep Quality', priority: 'Low Priority', score: 80 }
];

/**
 * AI Health Coach Component
 * 
 * Displays an AI-powered health coach interface with chat and insights
 * 
 * @param recentConversations - Array of recent conversations with the coach
 * @param userGoals - Array of user health goals
 * @param className - Additional CSS classes
 */
const AIHealthCoach: React.FC<AIHealthCoachProps> = ({
  recentConversations = [],
  userGoals = [],
  className = '',
}) => {
  const [newMessage, setNewMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  
  // Use the first conversation for demonstration, or empty array if none exist
  const activeConversationId = recentConversations.length > 0 ? recentConversations[0].id : null;
  const messages = activeConversationId ? mockConversationMessages[activeConversationId] || [] : [];
  
  // For demonstration, use mock focus areas
  const focusAreas = mockFocusAreas;
  
  // Get priority color
  const getPriorityColor = (priority: string) => {
    switch(priority) {
      case 'High Priority':
        return 'var(--status-error)';
      case 'Medium Priority':
        return 'var(--status-warning)';
      case 'Low Priority':
      default:
        return 'var(--clinical-normal)';
    }
  };
  
  // Handle message input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
  };
  
  // Handle form submission (message send)
  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (newMessage.trim() === '') return;
    
    // In a real app, this would send the message to an API
    console.log('Sending message:', newMessage);
    
    // Simulate AI typing response
    setIsTyping(true);
    setTimeout(() => {
      setIsTyping(false);
    }, 2000);
    
    // Clear input field
    setNewMessage('');
  };
  
  return (
    <HealthCard 
      title="AI Health Coach" 
      icon={MessageSquare} 
      className={`${className} border-t-4 border-t-blue-500`}
    >
      <div className="flex flex-col space-y-6">
        <div className="space-y-5 w-full">
          <div className="flex flex-col space-y-6 h-80 overflow-y-auto p-6 bg-blue-50/50 dark:bg-gray-900/30 rounded-lg">
            {messages.length > 0 ? (
              messages.map((message, index) => (
                <div 
                  key={message.id || index} 
                  className={`flex items-start ${message.sender === 'user' ? 'justify-end' : ''}`}
                >
                  {message.sender === 'coach' && (
                    <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-1">
                      <Activity className="h-5 w-5 text-white" />
                    </div>
                  )}
                  
                  <div 
                    className={`mx-4 p-4 rounded-lg max-w-[85%] ${
                      message.sender === 'coach' 
                        ? 'bg-white dark:bg-gray-800 shadow-sm text-gray-800 dark:text-gray-200' 
                        : 'bg-blue-100 dark:bg-blue-900/80 text-gray-800 dark:text-white'
                    }`}
                  >
                    <p className="text-sm leading-relaxed">{message.content}</p>
                    
                    {message.suggestions && message.suggestions.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Suggestions for you:</p>
                        <ul className="text-sm space-y-2.5">
                          {message.suggestions.map((suggestion, i) => (
                            <li key={i} className="flex items-center">
                              <span className="w-2 h-2 rounded-full bg-blue-500 mr-2.5 flex-shrink-0"></span>
                              <span className="text-gray-800 dark:text-gray-200">{suggestion}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {message.sender === 'user' && (
                    <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center flex-shrink-0 mt-1">
                      <span className="text-gray-700 dark:text-gray-300 text-xs font-medium">You</span>
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="text-center p-6 flex flex-col items-center justify-center h-full">
                <Activity className="h-8 w-8 text-blue-500 mb-3" />
                <p className="text-gray-600 dark:text-gray-300 mb-2 font-medium">
                  Start a conversation with your AI Health Coach
                </p>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  Get personalized guidance based on your health data
                </p>
              </div>
            )}
            
            {isTyping && (
              <div className="flex items-start">
                <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 mt-1">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                <div className="mx-4 p-3 rounded-lg bg-white dark:bg-gray-800 shadow-sm">
                  <div className="flex space-x-2">
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2.5 h-2.5 rounded-full bg-blue-600 animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <form onSubmit={handleSendMessage} className="flex space-x-3">
            <input
              type="text" 
              placeholder="Ask about your health goals, tracking progress, or get advice..." 
              value={newMessage}
              onChange={handleInputChange}
              className="flex-1 p-4 border rounded-lg bg-white dark:bg-gray-800 dark:text-white border-gray-300 dark:border-gray-700 focus:border-blue-500 dark:focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none text-base"
            />
            <button 
              type="submit"
              className="px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Send
            </button>
          </form>
        </div>
        
        <div className="space-y-4 w-full pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Zap className="h-4 w-4 text-blue-500 mr-2" />
              Weekly Focus Areas
            </h4>
            
            <div className="space-y-4">
              {focusAreas.length > 0 ? (
                focusAreas.map((area, index) => (
                  <div key={index} className="space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="font-medium text-gray-800 dark:text-gray-200">{area.name}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        area.priority === 'High Priority' 
                          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' 
                          : area.priority === 'Medium Priority'
                            ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                            : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                      }`}>
                        {area.priority}
                      </span>
                    </div>
                    <ProgressBar 
                      value={area.score} 
                      max={100} 
                      color={getPriorityColor(area.priority)} 
                    />
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-2">
                  No focus areas have been identified yet.
                </p>
              )}
            </div>
          </div>
          
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
            <h4 className="font-medium text-gray-900 dark:text-white mb-3 flex items-center">
              <Trophy className="h-4 w-4 text-blue-500 mr-2" />
              Your Health Goals
            </h4>
            
            <div className="space-y-2">
              {userGoals.length > 0 ? (
                userGoals.map((goal, index) => (
                  <div key={index} className="flex items-start">
                    <span className="w-2 h-2 rounded-full bg-blue-500 mr-2 mt-1.5"></span>
                    <span className="text-sm text-gray-700 dark:text-gray-300">{goal}</span>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 dark:text-gray-400 text-sm py-2">
                  You haven't set any health goals yet.
                </p>
              )}
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <button className="p-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-white rounded-lg flex flex-col items-center border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors shadow-sm">
              <Users className="h-5 w-5 mb-1 text-blue-600" />
              <span className="text-sm">Join Group Coaching</span>
            </button>
            <button className="p-3 bg-white dark:bg-gray-800 text-gray-800 dark:text-white rounded-lg flex flex-col items-center border border-gray-200 dark:border-gray-700 hover:border-blue-500 dark:hover:border-blue-500 transition-colors shadow-sm">
              <Calendar className="h-5 w-5 mb-1 text-blue-600" />
              <span className="text-sm">Schedule Check-in</span>
            </button>
          </div>
        </div>
      </div>
      
      <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
        The AI Health Coach provides personalized guidance based on your health data.
        All conversations are private and secure.
      </p>
    </HealthCard>
  );
};

export default AIHealthCoach; 